import type { FilaEntry, FilaResumo } from "@/config/types";
import { getQueueOrganizationRule } from "@/config/queueConstants";
import { getLocalStateFromName } from "@/utils/localState";
import type { ExperienciaFila, QueuePersonEntry, Region } from "./types";

// ─── Constantes ──────────────────────────────────────────────────

const QUEUE_MAX_PROGRESS_STORAGE_KEY = "queueMaxProgressByEntry";

// ─── Email ───────────────────────────────────────────────────────

export function normalizarEmail(email: string) {
  return email.trim().toLowerCase();
}

// ─── Região ──────────────────────────────────────────────────────

export function normalizeRegion(value: string | null): Region | null {
  const normalized = value?.trim().toUpperCase();

  if (normalized === "RJ" || normalized === "SP") {
    return normalized;
  }

  return null;
}

export function experienciaMatchesRegion(
  experiencia: { local?: { nome: string } },
  region: Region,
) {
  return getLocalStateFromName(experiencia.local?.nome) === region;
}

// ─── Progresso ───────────────────────────────────────────────────

function isEntradaAtiva(entry: FilaEntry) {
  return entry.status === "aguardando";
}

export function calcularProgresso(
  posicaoAtiva: number,
  totalPessoasAtivas: number,
) {
  if (posicaoAtiva <= 1) return 100;

  const totalSeguro = Math.max(totalPessoasAtivas, 1);
  const posicaoSegura = Math.min(Math.max(posicaoAtiva, 1), totalSeguro);

  return Math.max(
    0,
    Math.min(
      100,
      Math.round(((totalSeguro - posicaoSegura + 1) / totalSeguro) * 100),
    ),
  );
}

// ─── Status visual da fila ───────────────────────────────────────

export function getQueueStatus(queue: QueuePersonEntry) {
  const orgFila = queue.experiencia?.orgFila || 1;
  const rules = getQueueOrganizationRule(orgFila);
  const posicao = queue.pessoasNaFrente + 1;

  if (posicao <= rules.called) {
    return {
      isCalled: true,
      progress: 100,
      status: {
        tone: "success" as const,
        title: "É HORA DE IR PARA A FILA FÍSICA.",
        text: "Você tem até 10 minutos para se apresentar e aguardar a sua vez.",
      },
    };
  }

  if (posicao <= rules.approaching) {
    return {
      isCalled: false,
      progress: Math.max(queue.progress, 86),
      status: {
        // tone: "info" as const,
        // title: "Sua vez está chegando.",
        // text: "Fique de olho nesta página.",
        tone: "warning" as const,
        title: "AGUARDE SUA CHAMADA PARA ENTRAR NA FILA FÍSICA.",
        text: "Avisaremos por aqui quando chegar a hora.",
      },
    };
  }

  return {
    isCalled: false,
    progress: queue.progress,
    status: {
      tone: "warning" as const,
      title: "AGUARDE SUA CHAMADA PARA ENTRAR NA FILA FÍSICA",
      text: "Avisaremos por aqui quando chegar a hora.",
    },
  };
}

// ─── Construção de filas a partir do Firebase ────────────────────

export function buildQueuesFromFirebase(
  filas: Record<string, FilaResumo>,
  participantEmail: string,
  experienciasById: Map<string, ExperienciaFila>,
): QueuePersonEntry[] {
  const emailNormalizado = normalizarEmail(participantEmail);

  return Object.entries(filas).flatMap(([firebaseEventoId, fila]) => {
    const entries = Object.entries(fila.entries ?? {})
      .map(([entryKey, filaEntry]) => ({ entryKey, ...filaEntry }))
      .sort((a, b) => a.posicao - b.posicao);
    const entradasAtivas = entries.filter(isEntradaAtiva);
    const entradaDaPessoa = entradasAtivas.find(
      (entry) => normalizarEmail(entry.email) === emailNormalizado,
    );

    if (!entradaDaPessoa) return [];

    const eventoId = fila.eventoId || firebaseEventoId;
    const posicaoAtiva =
      entradasAtivas.findIndex(
        (entry) => entry.entryKey === entradaDaPessoa.entryKey,
      ) + 1;

    return [
      {
        ...entradaDaPessoa,
        eventoId,
        filaStatus: fila.status ?? "ativa",
        posicao: posicaoAtiva,
        progress: calcularProgresso(posicaoAtiva, entradasAtivas.length),
        pessoasNaFrente: Math.max(posicaoAtiva - 1, 0),
        totalPessoasAtivas: entradasAtivas.length,
        experiencia: experienciasById.get(eventoId) ?? null,
      },
    ];
  });
}

// ─── localStorage — progresso máximo ─────────────────────────────

export function getStoredMaxProgress(): Record<string, number> {
  try {
    const storedValue = localStorage.getItem(QUEUE_MAX_PROGRESS_STORAGE_KEY);
    if (!storedValue) return {};

    const parsedValue = JSON.parse(storedValue);
    return parsedValue && typeof parsedValue === "object"
      ? (parsedValue as Record<string, number>)
      : {};
  } catch {
    return {};
  }
}

export function setStoredMaxProgress(value: Record<string, number>) {
  localStorage.setItem(QUEUE_MAX_PROGRESS_STORAGE_KEY, JSON.stringify(value));
}
